import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef} from 'react';
import {currencyForUser, currencyPreferencesForRead, parseCurrencyPreferences} from '@liquid-spirit/api/currency-preferences';
import {formatTreasuryAmount, treasuryUnit} from '@liquid-spirit/api/treasury-figures';
import {readDeviceDisplayContext} from '@liquid-spirit/date-time/display';
import {subscribeCurrencyRefresh} from './currency-lifecycle';

const CurrencyContext = createContext(null);
const accountId = user => String(user?.id || user?._id || '');
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
export function CurrencyProvider({user, token, readProfile, saveProfile, onPreferences, children}) {
  return React.createElement(ScopedCurrencyProvider, {user, token, readProfile, saveProfile, onPreferences}, children);
}
function ScopedCurrencyProvider({user, token, readProfile, saveProfile, onPreferences, children}) {
  const scope = `${accountId(user)}:${token || ''}`;
  const latest = useRef(); latest.current = {user, token, readProfile, saveProfile, onPreferences, scope};
  const version = useRef(0), saving = useRef(null), alive = useRef(true);
  // The server owns account preferences. Refresh when another app may have saved
  // them, retaining the last confirmed value offline and discarding stale reads.
  useEffect(() => {
    alive.current = true;
    const refresh = async () => {
      const current = latest.current;
      if (!current.token || !accountId(current.user) || !current.readProfile || saving.current?.scope === current.scope) return;
      const request = ++version.current;
      try {
        const profile = await current.readProfile(current.token);
        if (!alive.current || request !== version.current || current.token !== latest.current.token || !same(current.user?.currencyPreferences, latest.current.user?.currencyPreferences) || saving.current?.scope === current.scope || accountId(profile) !== accountId(latest.current.user) || !profile.currencyPreferences) return;
        const confirmed = parseCurrencyPreferences(profile.currencyPreferences);
        if (!same(confirmed, latest.current.user?.currencyPreferences)) latest.current.onPreferences?.(confirmed);
      } catch { /* Offline refresh must not overwrite a confirmed account preference. */ }
    };
    refresh();
    const unsubscribe = subscribeCurrencyRefresh(refresh);
    return () => {alive.current = false; unsubscribe();};
  }, [scope]);
  const savePreferences = useCallback(async value => {
    const current = latest.current;
    if (saving.current?.scope === current.scope) throw new Error('A currency save is already in progress.');
    if (!current.token || !accountId(current.user) || !current.saveProfile) throw new Error('Sign in to save your currency.');
    const preference = parseCurrencyPreferences(value);
    const operation = {scope: current.scope};
    saving.current = operation; version.current++;
    try {
      const profile = await current.saveProfile(current.token, preference);
      if (!alive.current || saving.current !== operation || accountId(profile) !== accountId(latest.current.user)) throw new Error('Your account changed. Open settings again.');
      if (!profile.currencyPreferences) throw new Error('Currency settings are not available on the server yet.');
      const confirmed = parseCurrencyPreferences(profile.currencyPreferences);
      if (!same(preference, confirmed)) throw new Error('The server did not confirm your currency change. Try again.');
      latest.current.onPreferences?.(confirmed);
      return confirmed;
    } finally {if (saving.current === operation) saving.current = null;}
  }, []);
  const value = useMemo(() => ({user, enabled: Boolean(token && accountId(user)), savePreferences}), [user, token, savePreferences]);
  return React.createElement(CurrencyContext.Provider, {value}, children);
}
export function useCurrencyDisplay() {
  const context = useContext(CurrencyContext);
  let locale;
  try {locale = context?.user?.dateTimePreferences?.locale || (context ? readDeviceDisplayContext().locale : 'en-AU');} catch {locale = 'en-AU';}
  const currency = currencyForUser(context?.user, locale);
  return useMemo(() => ({
    currency, locale, user: context?.user,
    preference: currencyPreferencesForRead(context?.user?.currencyPreferences),
    enabled: context?.enabled || false,
    savePreferences: context?.savePreferences,
    formatAmount: (value, fund) => formatTreasuryAmount(value, fund, {currency, locale}),
    unitLabel: fund => {const unit = treasuryUnit(fund); return unit.assetType === 'fiat' && unit.currency === currency ? '' : unit.label;},
    amountLabel: (label, fund) => {const unit = treasuryUnit(fund); return unit.assetType === 'fiat' && unit.currency === currency ? label : `${label} (${unit.label})`;},
  }), [currency, locale, context]);
}
